import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

function buildURLSearchParams(
  initialSearch: URLSearchParams,
  { paramName, paramValue }: { paramName: string; paramValue: any },
  options: UpdateActionOptions = {
    filterNullish: true,
    resetPagination: true,
  }
): URLSearchParams {
  const newSearch = new URLSearchParams(initialSearch);

  const isEmptyArray = Array.isArray(paramValue) && paramValue.length === 0;
  const isNullishValue = options.filterNullish && (!paramValue || isEmptyArray);
  const isValidNumber = typeof paramValue === 'number';
  const isValidBoolean = typeof paramValue === 'boolean';
  const isValidDate = paramValue instanceof Date;

  if (isNullishValue && !isValidNumber && !isValidBoolean) newSearch.delete(paramName);
  else if (isValidDate) {
    const dateAsString = `${paramValue.getFullYear()}-${(paramValue.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${paramValue.getDate().toString().padStart(2, '0')}`;
    newSearch.set(paramName, dateAsString);
  } else newSearch.set(paramName, paramValue?.toString() || '');

  return newSearch;
}

function resetPagination(
  searchParams: URLSearchParams,
  paginationFields: {
    page: string;
    pageSize: string;
  }
): URLSearchParams {
  const newSearchParams = new URLSearchParams(searchParams);

  newSearchParams.delete(paginationFields.page);

  return newSearchParams;
}

type UpdateActionOptions = {
  filterNullish?: boolean;
  resetPagination?: boolean;
  /** Replace the current history entry instead of pushing one (e.g. debounced typing). */
  replace?: boolean;
};

function navigateToSearch(navigate: ReturnType<typeof useNavigate>, searchParams: URLSearchParams, replace?: boolean) {
  const target = `?${searchParams.toString()}`;
  if (replace) navigate(target, { replace: true });
  else navigate(target);
}

function useQueryStringParameters<T extends Record<string, any>, U extends Record<string, any> = T>(
  parser: (searchParams: Record<string, string>) => T,
  paginationFields?: {
    page: string;
    pageSize: string;
  }
) {
  const paginationFieldsWithDefaults = {
    page: paginationFields?.page || 'page',
    pageSize: paginationFields?.pageSize || 'pageSize',
  };

  const { search } = useLocation();
  const navigate = useNavigate();

  const queryStrings: T = React.useMemo(() => {
    const searchParams = new URLSearchParams(search);
    const searchParamsObject = Object.fromEntries(searchParams.entries());
    return parser(searchParamsObject);
  }, [search]);

  const updateParameter = React.useCallback(
    (
      paramName: string,
      paramValue: any,
      options: UpdateActionOptions = {
        filterNullish: true,
        resetPagination: true,
      }
    ) => {
      let searchParams = buildURLSearchParams(new URLSearchParams(search), { paramName, paramValue }, options);

      if (options.resetPagination) {
        searchParams = resetPagination(searchParams, paginationFieldsWithDefaults);
      }

      navigateToSearch(navigate, searchParams, options.replace);
    },
    [search]
  );

  const updateMultipleParameters = React.useCallback(
    (
      newParameters: U,
      options: UpdateActionOptions = {
        filterNullish: true,
        resetPagination: true,
      }
    ) => {
      let searchParams = new URLSearchParams(search);

      Object.entries(newParameters).forEach(([paramName, paramValue]) => {
        searchParams = buildURLSearchParams(searchParams, { paramName, paramValue }, options);
      });

      if (options.resetPagination) {
        searchParams = resetPagination(searchParams, paginationFieldsWithDefaults);
      }

      navigateToSearch(navigate, searchParams, options.replace);
    },
    [search]
  );

  function clearAllParameters() {
    navigate('');
  }

  return { queryStrings, updateParameter, updateMultipleParameters, clearAllParameters };
}

export default useQueryStringParameters;
